import {
  Component,
  OnInit,
  OnChanges,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild
} from '@angular/core';
import { MeteringGroupFormComponent } from '@metering/components/metering-group-form/metering-group-form.component';
import { MeteringType, PermissionType, MeteringFilterType, UserSettingOption, DisturbanceType } from '@enums';
import {
  Customer,
  MeteringPoint,
  MeteringPointAddress,
  MeteringFilter,
  UserSettings,
  UserInfo,
  DisruptionInfo,
  MeteringPointFilters,
  MeteringPointFilterSettings,
  MeteringPointSettings
} from '@model';
import { SxDialogService, SxPermissionService, SxExportService, SxRouterExtService } from '@services';
import { SearchFilterDataService } from '@appStore/metering-points/search-filter-data-service';
import { containsCaseInsensitive } from '@utils/sx-utilities';
import { debounceTime, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import * as fromStore from '@appStore/metering-points';
import * as fromAccountStore from '@appStore/account';
import { Store } from '@ngrx/store';
import { MeteringPointService } from '@appStore/metering-points/services';
import { MeteringpointPaneTableDataSource } from './metering-points-pane-datasource';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Observable } from 'rxjs';
import { ViewportScroller } from '@angular/common';
import { ToLocalDateTimePipe } from '@utils/pipes';
import { TranslateService } from '@ngx-translate/core';


export class CustomerMeteringView {
  id: string;
  name: string;
  meteringType: MeteringType;
  meteringPoints?: MeteringPoint[];
  _expanded?: boolean;
  isWhiteLabel?: boolean;
  externalOrganisationId?: number;
  alias?: string;
}

@Component({
    selector: 'app-metering-points-pane',
    templateUrl: './metering-points-pane.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MeteringPointsPaneComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(MatPaginator, { static: true })
  paginator: MatPaginator;

  @ViewChild(MatSort, { static: true })
  sort: MatSort;

  @Input()
  meteringPointSettings: MeteringPointSettings; // Filter settings (Hot/Cold) selected by user
  @Input()
  paneType: MeteringType;
  @Input()
  customersWithMeteringPoints: Customer[];
  @Input()
  selectedMeteringPointIds: number[];
  @Input()
  virtualGroupActivated: boolean;
  @Input()
  userSettings: UserSettings;
  @Input()
  userInfo: UserInfo;
  @Input()
  filterArea: string;
  @Input()
  isMobile: boolean;

  @Output()
  meteringPointSelected = new EventEmitter<MeteringPoint>();
  @Output()
  meteringPointFilterEvent = new EventEmitter<MeteringPointFilterSettings>();

  searchFilter: MeteringPointFilters;
  filter: MeteringPointFilterSettings;

  DisturbanceTypeEnum = DisturbanceType;

  customersWithMeteringPointsView: CustomerMeteringView[];
  customersWithMeteringPointsViewLoaded: boolean;
  dataSource: MeteringpointPaneTableDataSource;

  groupingEnabled: boolean;

  PermissionEnum = PermissionType;
  hasManageGroupPermission = true;
  isExporting = false;

  private meteringPointsCount: number;
  private subs = new SubSink();
  private allMeteringPoints: MeteringPoint[] = [];
  tableView = false;
  sortActive: string;
  sortDirection: string;
  filterActive = false;
  toggleAll = false;

  displayedColumns = ['select', 'meteringPointId', 'meteringPointName', 'meteringPointAddress', 'meteringPointGroups'];
  selectedPageIndex$: Observable<number>;
  currentPage: number;
  paginationInitialized = false;
  pageSize: number;
  scrollPosition: [number, number] = [0, 0];
  previousRoute: string;

  constructor(
    private sxDialogService: SxDialogService,
    private changeDetectorRef: ChangeDetectorRef,
    private filterData: SearchFilterDataService,
    private sxPermissionService: SxPermissionService,
    private store: Store<fromStore.State>,
    private accountStore: Store<fromAccountStore.State>,
    private meteringPointService: MeteringPointService,
    private exportService: SxExportService,
    private scroller: ViewportScroller,
    private routerService: SxRouterExtService,
    private translate: TranslateService,
    private toLocalDate: ToLocalDateTimePipe
  ) {
  }

  ngOnInit() {
    this.customersWithMeteringPointsViewLoaded = false;
    this.hasManageGroupPermission = this.sxPermissionService.hasUserManageMeteringPointGroupsPermission();
    
    // Recieve updates on the search filter
    this.subs.sink = this.filterData.currentSearchFilter.pipe(debounceTime(300)).subscribe((searchFilter) => {
      this.searchFilter = searchFilter;
      if (searchFilter.hotMeteringPointFilter.searchPhrase !== '') {
        this.currentPage = 0;
          this.store.dispatch(
            new fromStore.SelectPageIndexHotMeteringPoint(
              this.currentPage,
              this.pageSize,
              this.sort.active,
              this.sort.direction,
              this.scrollPosition)
            );
      }
      if (searchFilter.coldMeteringPointFilter.searchPhrase !== '') {
          this.store.dispatch(
            new fromStore.SelectPageIndexColdMeteringPoint(
              this.currentPage,
              this.pageSize,
              this.sort.active,
              this.sort.direction,
              this.scrollPosition)
          );
      } else {
        this.createLocalView(this.meteringPointSettings);
      }
      this.isAllSelected();
    });

    if (this.paneType === MeteringType.Hot) {
      this.subs.sink = this.store
        .select(fromStore.getSelectedHotMeteringPointsPageIndex)
        .pipe(take(1))
        .subscribe((i) => {
          this.currentPage = i.pageIndex;
          this.paginator.pageSize = i.pageSize;
          this.pageSize = i.pageSize;
          this.sort.direction = i.sortDirection === 'desc' ? 'desc' : 'asc';
          this.sort.active = i.sortColumn;
          this.scrollPosition = i.scrollPosition;
          this.sort.sortChange.emit(); // Needed to update the sorting direction in UI
        });
        this.createLocalView(this.meteringPointSettings);
    }
    if (this.paneType === MeteringType.Cold) {
      this.subs.sink = this.store
        .select(fromStore.getSelectedColdMeteringPointsPageIndex)
        .pipe(take(1))
        .subscribe((i) => {
          this.currentPage = i.pageIndex;
          this.paginator.pageSize = i.pageSize;
          this.pageSize = i.pageSize;
          this.sort.direction = i.sortDirection === 'desc' ? 'desc' : 'asc';
          this.sort.active = i.sortColumn;
          this.scrollPosition = i.scrollPosition;
          this.sort.sortChange.emit(); // Needed to update the sorting direction in UI
        });
        this.createLocalView(this.meteringPointSettings);
    }
  }

  // Runs whenever the input prameters changes
  ngOnChanges() {

      if (this.paneType === MeteringType.Hot) {
        this.subs.sink = this.store
          .select(fromStore.getSelectedHotMeteringPointsPageIndex)
          .pipe(take(1))
          .subscribe((i) => {
            this.currentPage = i.pageIndex;
            this.paginator.pageSize = i.pageSize;
            this.pageSize = i.pageSize;
            this.sort.direction = i.sortDirection === 'desc' ? 'desc' : 'asc';
            this.sort.active = i.sortColumn;
            this.scrollPosition = i.scrollPosition;
            this.sort.sortChange.emit(); // Needed to update the sorting direction in UI
          });
          this.createLocalView(this.meteringPointSettings);
      }
  
      if (this.paneType === MeteringType.Cold) {
        this.subs.sink = this.store
          .select(fromStore.getSelectedColdMeteringPointsPageIndex)
          .pipe(take(1))
          .subscribe((i) => {
            this.currentPage = i.pageIndex;
            this.paginator.pageSize = i.pageSize;
            this.pageSize = i.pageSize;
            this.sort.direction = i.sortDirection === 'desc' ? 'desc' : 'asc';
            this.sort.active = i.sortColumn;
            this.scrollPosition = i.scrollPosition;
            this.sort.sortChange.emit(); // Needed to update the sorting direction in UI
          });
          this.createLocalView(this.meteringPointSettings);
      }
      this.setTableView();
  
      setTimeout(() => {
        if (this.routerService.getPreviousUrl()?.includes('secure/meteringpoints/')) {
            this.scroller.scrollToPosition(this.scrollPosition);
        }
      }, 1000);
  }

  meteringPointsFilterEventHandler(filterSettings: MeteringPointFilterSettings) {
    this.store.dispatch(new fromStore.MeteringPointsFilterSave(this.meteringPointFilterSettings(filterSettings)));
    this.changeDetectorRef.detectChanges();
    //this.updateContractsFilter(filterSettings, false);
  }

  meteringPointFilterSettings(filterSettings: MeteringPointFilterSettings) {

    let clone = {
        ...this.meteringPointSettings,
        hotMeteringPointFilterSettings:
          this.paneType === MeteringType.Hot
            ? {
                ...filterSettings,
                miscMultiChoiceFilter: filterSettings.miscMultiChoiceFilter
              }
            : this.meteringPointSettings.hotMeteringPointFilterSettings,
        coldMeteringPointFilterSettings:
          this.paneType === MeteringType.Cold
            ? {
              ...filterSettings,
              miscMultiChoiceFilter: filterSettings.miscMultiChoiceFilter
              }
            : this.meteringPointSettings.coldMeteringPointFilterSettings
      } as MeteringPointSettings;
    return clone;
  }

  meteringPointsViewHandler(value: boolean) {
  
    this.tableView = !this.tableView;
    let tableViewSetting = this.tableView ? UserSettingOption.Active : UserSettingOption.Inactive;

    let newUserSettings = {
      ...this.userSettings,
      meteringpointTableview: tableViewSetting
    };

    // do not try to save virtual filter if it´s included in the user settings
    if (newUserSettings.hotMeteringFilter.filterType === MeteringFilterType.VirtualFilter) {
      newUserSettings.hotMeteringFilter.filterType = MeteringFilterType.Unfiltered;
    }
    if (newUserSettings.coldMeteringFilter.filterType === MeteringFilterType.VirtualFilter) {
      newUserSettings.coldMeteringFilter.filterType = MeteringFilterType.Unfiltered;
    }

    // Reset scroll position
    this.store.dispatch(new fromStore.ClearScrollPosition());

    this.accountStore.dispatch(
      new fromAccountStore.SettingUpdate({
        userSettings: newUserSettings,
        userId: this.userInfo.id,
        filterArea: this.filterArea,
        meteringType: this.paneType,
        meteringPoints: null
      })
    );

    this.isAllSelected();
    this.changeDetectorRef.detectChanges();
  }

  toggleAllItems(value: boolean) {
    this.toggleAll = !value;
    for (let x of this.dataSource.data) {
      x.selected = this.toggleAll;
      this.selectMeteringPoint(x);
    }
  }

  isAllSelected() {
    this.toggleAll = this.dataSource?.data.every(function(item: MeteringPoint) {
      return item.selected === true;
    });
  }

  filterDisruption(disruptions: DisruptionInfo[]){
    if (disruptions){
      return disruptions.find(di => !di.isRestored);
    }
    return null;
  }

  containedMeteringPointsHasFunctionControl(meteringPoint: MeteringPoint): boolean {
    return meteringPoint.type === 'LOG' && meteringPoint.containedMeteringPoints?.some(mp => mp.hasFunctionControl);
  }

  onPagination(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  setTableView(): boolean {
    if (this.isMobile) return false;
    
    switch (this.userSettings?.meteringpointTableview) {
      case UserSettingOption.Active:
        return true;
      case UserSettingOption.Inactive:
        return false;
      case UserSettingOption.Notset:
        return this.allMeteringPoints.length > 12;
      default:
        //
        return false;
    }
  }

  ngOnDestroy() {
    this.scrollPosition = this.scroller.getScrollPosition();

    if (this.paneType === MeteringType.Hot) {
      this.store.dispatch(
        new fromStore.SelectPageIndexHotMeteringPoint(
          this.currentPage,
          this.pageSize,
          this.sort.active,
          this.sort.direction,
          this.scrollPosition)
      );
    }

    if (this.paneType === MeteringType.Cold) {
      this.store.dispatch(
        new fromStore.SelectPageIndexColdMeteringPoint(
          this.currentPage,
          this.pageSize,
          this.sort.active,
          this.sort.direction,
          this.scrollPosition)
      );
    }
    this.subs.unsubscribe();
  }

  panelOpened(customer: Customer) {
    this.customersWithMeteringPointsView.map((item) => (item.id === customer.id ? (item._expanded = true) : item));
  }

  panelClosed(customer: Customer) {
    this.customersWithMeteringPointsView.map((item) => (item.id === customer.id ? (item._expanded = false) : item));
  }

  createLocalView = (_filter: MeteringPointSettings) => {
    this.meteringPointSettings = { ..._filter };

    switch (this.paneType) {
      case MeteringType.Hot:
        this.filter = this.meteringPointSettings.hotMeteringPointFilterSettings;
        break;
      case MeteringType.Cold:
        this.filter = this.meteringPointSettings.coldMeteringPointFilterSettings;
        break;
    }

    if (!this.customersWithMeteringPoints || this.customersWithMeteringPoints.length === 0 ) {
      this.customersWithMeteringPointsView = [];
      return;
    }

    this.meteringPointsCount = 0;
    this.allMeteringPoints = [];
    this.customersWithMeteringPoints.forEach((customer) => {
      switch (this.paneType) {
        case MeteringType.Hot:
          this.meteringPointsCount += customer.meteringPointsHeat.length;
          this.allMeteringPoints = [...this.allMeteringPoints, ...customer.meteringPointsHeat];
          break;
        case MeteringType.Cold:
          this.meteringPointsCount += customer.meteringPointsCold.length;
          this.allMeteringPoints = [...this.allMeteringPoints, ...customer.meteringPointsCold];
          break;
      }
    });
    this.groupingEnabled = this.meteringPointsCount > 1;

    let filteredCustomersWithMeteringPointsView = this.applyFilterSettings(this.customersWithMeteringPoints, this.filter);

    let filteredCustomers = this.filterCustomers(filteredCustomersWithMeteringPointsView);
    this.customersWithMeteringPointsView = filteredCustomers.map((customer: Customer) =>
      this.mapFromCustomer(customer, this.paneType)
    );

    this.customersWithMeteringPointsViewLoaded = true;

    this.paginator.pageSize = this.pageSize;
    this.paginator.pageIndex = this.currentPage;

    this.dataSource = new MeteringpointPaneTableDataSource(
      this.paginator,
      this.sort,
      this.customersWithMeteringPointsView.flatMap((c) => c.meteringPoints)
    );
    this.tableView = this.setTableView();
    this.isAllSelected();
    // Force the view to update
    this.changeDetectorRef.detectChanges();
  };

  applyFilterSettings(_data: Customer[], _filter: MeteringPointFilterSettings) : Customer[] {
  
      if (!_filter) {
        return _data;
      }

      let filteredCustomers = [] as Customer[];

  
      for (let c = 0; c < _data.length; c++) {
        let newCustomer = {
          ..._data[c],
          meteringPointsHeat: this.paneType === MeteringType.Hot ? [..._data[c].meteringPointsHeat] : [],
          meteringPointsCold: this.paneType === MeteringType.Cold ? [..._data[c].meteringPointsCold] : []
        };

        switch (this.paneType) {
          case MeteringType.Hot:

            // Filter on Miscellaneous (Logical, Function Control, Intelligy)
            if (_filter.miscMultiChoiceFilter) {
              // Apply filter on MeteringPoint, column: 
              // type (Logical),
              // hasFunctionControl (Function control), 
              // hasIntelligy (Intelligy) 
              let selected = _filter.miscMultiChoiceFilter.selected;
              if (selected && selected.length > 0) {
               
                  newCustomer.meteringPointsHeat = newCustomer.meteringPointsHeat.filter((mp) =>
                    this.filterMeteringPointByMisc(mp, selected)
                  );
                }
                newCustomer.meteringPointsHeat.sort((a, b) => this.sortMeteringPointsByName(a, b));
            }
            break;
          case MeteringType.Cold:
                  
            if (_filter.miscMultiChoiceFilter) {
              let selected = _filter.miscMultiChoiceFilter.selected;
              if (selected && selected.length > 0) {
                       
                newCustomer.meteringPointsCold = newCustomer.meteringPointsCold.filter((mp) =>
                  this.filterMeteringPointByMisc(mp, selected)
                );
              }
              newCustomer.meteringPointsCold.sort((a, b) => this.sortMeteringPointsByName(a, b));
            }
            break;
        }
  
        if (newCustomer.meteringPointsCold.length > 0 || newCustomer.meteringPointsHeat.length > 0) {
          filteredCustomers.push(newCustomer);
        }
      }
  
      return filteredCustomers;
  }

  filterCustomers = (customers: Customer[]) => {
    let filteredCustomers = [] as Customer[];

    for (let c = 0; c < customers.length; c++) {
      let newCustomer = {
        ...customers[c],
        meteringPointsHeat: this.paneType === MeteringType.Hot ? [...customers[c].meteringPointsHeat] : [],
        meteringPointsCold: this.paneType === MeteringType.Cold ? [...customers[c].meteringPointsCold] : []
      };

      switch (this.paneType) {
        case MeteringType.Hot:
          if (this.searchFilter && this.searchFilter.hotMeteringPointFilter.searchPhrase.length > 0) {
            // Only filter if there is a search phrase
            newCustomer.meteringPointsHeat = newCustomer.meteringPointsHeat.filter((mp) =>
              this.filterMeteringPoint(mp, newCustomer)
            );
          }
          newCustomer.meteringPointsHeat.sort((a, b) => this.sortMeteringPointsByName(a, b));
          break;
        case MeteringType.Cold:
          if (this.searchFilter && this.searchFilter.coldMeteringPointFilter.searchPhrase.length > 0) {
            // Only filter if there is a search phrase
            newCustomer.meteringPointsCold = newCustomer.meteringPointsCold.filter((mp) =>
              this.filterMeteringPoint(mp, newCustomer)
            );
          }
          newCustomer.meteringPointsCold.sort((a, b) => this.sortMeteringPointsByName(a, b));
          break;
      }

      if (newCustomer.meteringPointsCold.length > 0 || newCustomer.meteringPointsHeat.length > 0) {
        filteredCustomers.push(newCustomer);
      }
    }

    return filteredCustomers;
  };

  filterMeteringPointByMisc = (mp: MeteringPoint, selected: string[]): boolean => {
    if (selected.includes(this.translate.instant('meteringpoints.filter.misc.hasLogical')) && mp.type === 'LOG') {
      return true;
    } 
    if (selected.includes(this.translate.instant('meteringpoints.filter.misc.hasFunctionControl')) && mp.hasFunctionControl) {
      return true;
    }
    if (selected.includes(this.translate.instant('meteringpoints.filter.misc.hasIntelligy')) && mp.hasIntelligy) {
      return true;
    }
    return false;
  }

  filterMeteringPoint = (mp: MeteringPoint, customer: Customer): boolean => {
    if (this.selectedMeteringPointIds.some((smpId) => smpId === mp.id)) {
      return true;
    }
    if (this.paneType === MeteringType.Hot) {
      if (containsCaseInsensitive(mp.name, this.searchFilter.hotMeteringPointFilter.searchPhrase)) {
        return true;
      }

      if (containsCaseInsensitive(customer.name, this.searchFilter.hotMeteringPointFilter.searchPhrase)) {
        return true;
      }

      if (this.isMeteringPointInGroupByname(mp.parentGroups)) {
        return true;
      }

      if (this.foundInAdresses(mp.addresses, this.searchFilter.hotMeteringPointFilter.searchPhrase)) {
        return true;
      }

      if (containsCaseInsensitive(mp.id.toString(), this.searchFilter.hotMeteringPointFilter.searchPhrase)) {
        return true;
      }
    }
    if (this.paneType === MeteringType.Cold) {
      if (containsCaseInsensitive(mp.name, this.searchFilter.coldMeteringPointFilter.searchPhrase)) {
        return true;
      }

      if (containsCaseInsensitive(customer.name, this.searchFilter.coldMeteringPointFilter.searchPhrase)) {
        return true;
      }

      if (this.isMeteringPointInGroupByname(mp.parentGroups)) {
        return true;
      }

      if (this.foundInAdresses(mp.addresses, this.searchFilter.coldMeteringPointFilter.searchPhrase)) {
        return true;
      }

      if (containsCaseInsensitive(mp.id.toString(), this.searchFilter.coldMeteringPointFilter.searchPhrase)) {
        return true;
      }
    }

    return false;
  };

  foundInAdresses = (addresses: MeteringPointAddress[], filterString: string): boolean => {
    const matched = addresses.filter((address) =>
      containsCaseInsensitive(address.street + ' ' + address.streetNo + ' ' + address.zip + ' ' + address.city, filterString)
    );
    return matched.length > 0;
  };

  isMeteringPointInGroupByname = (parentGroups: { name: string; id: number }[]): boolean => {
    if (!parentGroups || parentGroups.length === 0) {
      return false;
    }

    for (let pg of parentGroups) {
      if (containsCaseInsensitive(pg.name, this.paneType === MeteringType.Hot ? 
        this.searchFilter.hotMeteringPointFilter.searchPhrase :
        this.searchFilter.coldMeteringPointFilter.searchPhrase)) {
        return true;
      }
    }

    return false;
  };

  createNewGroup = () => {
    let selectedMeteringPoints = this.allMeteringPoints.filter((mp) =>
      this.selectedMeteringPointIds.some((mpId) => mpId === mp.id)
    );

    let dialogRef = this.sxDialogService.openDialog(MeteringGroupFormComponent, {
      selectedMeteringPoints: selectedMeteringPoints,
      meteringType: this.paneType
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result: string) => {
      if (!!result && result === 'save' && !this.virtualGroupActivated) {
        this.toggleAllVisible(false);
        this.changeDetectorRef.detectChanges();
      }
    });

    // Reset scroll position
    this.store.dispatch(new fromStore.ClearScrollPosition());
  };

  createVirtualGroup = () => {
    this.setSortAndPaging();
    this.store.dispatch(new fromStore.ActivateVirtualGroup(this.paneType));
    this.accountStore.dispatch(
      new fromAccountStore.FilterSettingUpdate({
        filter: this.getVirtualFilter(this.selectedMeteringPointIds),
        meteringType: this.paneType,
        userId: this.userInfo.id,
        filterArea: this.filterArea,
        meteringPoints: this.allMeteringPoints
      })
    );
    this.store.dispatch(
      new fromStore.SetSelection({ meteringPointIds: this.selectedMeteringPointIds, meteringType: this.paneType })
    );

   // Reset scroll position
   this.store.dispatch(new fromStore.ClearScrollPosition());
  };

  removeVirtualGroup = () => {
    this.setSortAndPaging();
    this.store.dispatch(new fromStore.DeactivateVirtualGroup(this.paneType));
    this.accountStore.dispatch(new fromAccountStore.FilterSettingReset(this.paneType));
    // Reset scroll position
    this.store.dispatch(new fromStore.ClearScrollPosition());
  };

  selectTableMeteringPoint(event: MatCheckboxChange, meteringPoint: MeteringPoint) {
    meteringPoint.selected = event.checked;
    this.selectMeteringPoint(meteringPoint);
    this.isAllSelected();
  }

  isWhiteLabelCustomer(meteringPoint: MeteringPoint): boolean {

    let _customer = this.customersWithMeteringPointsView.find((c) => c.id === meteringPoint.customerId);
    return _customer ? _customer.isWhiteLabel : false;
  }

  onMeteringPointCheckboxClick(event: any){
    event.stopPropagation();
  }

  onSelectCardMeteringPoint = (event: MatCheckboxChange, meteringPoint: MeteringPoint) => {
    meteringPoint.selected = event.checked;
    this.selectMeteringPoint(meteringPoint);
  };

  selectMeteringPoint = (meteringPoint: MeteringPoint) => {
    let selectedMpIds = this.selectedMeteringPointIds.filter((x) => x !== meteringPoint.id);
    if (meteringPoint.selected) {
      selectedMpIds.push(meteringPoint.id);
    }
    if (this.virtualGroupActivated) {
      if (selectedMpIds.length === 0) {
        this.accountStore.dispatch(new fromAccountStore.FilterSettingReset(this.paneType));
      } else {
        this.accountStore.dispatch(
          new fromAccountStore.FilterSettingUpdate({
            filter: this.getVirtualFilter(selectedMpIds),
            meteringType: this.paneType,
            userId: this.userInfo.id,
            filterArea: this.filterArea,
            meteringPoints: this.allMeteringPoints
          })
        );
      }
    }
    // Only use Store when managing virtual groups... otherwise the UI gets very slow for customers with many MeterinPoints
    this.selectedMeteringPointIds = selectedMpIds;
  };

  getVirtualFilter = (selectedMpIds: number[]): MeteringFilter =>
    <MeteringFilter>{
      id: -1,
      filterId: -1,
      meteringType: this.paneType,
      filterType: MeteringFilterType.VirtualFilter,
      containsMeteringPointIds: [...selectedMpIds],
      subtitle: '' + selectedMpIds.length
    };

  toggleAllVisible = (visible: boolean) => {
    this.customersWithMeteringPointsView = this.customersWithMeteringPointsView.map(
      (customer: CustomerMeteringView) => {
        if(!customer.isWhiteLabel) {
          return {
            ...customer,
            meteringPoints: [
              ...customer.meteringPoints.map((meteringPoint: MeteringPoint) => ({ ...meteringPoint, selected: visible }))
            ]
          };
        } else {
          return {
            ...customer,
            meteringPoints: [
              ...customer.meteringPoints.map((meteringPoint: MeteringPoint) => ({ ...meteringPoint, selected: false }))
            ]
          };
        }
      }
    );

    let selectedMpIds = this.customersWithMeteringPointsView
      .map((customer: CustomerMeteringView) => customer.meteringPoints.filter((mp) => mp.selected))
      .reduce((a, b) => a.concat(b))
      .map((mp) => mp.id);

    this.store.dispatch(new fromStore.SetSelection({ meteringPointIds: selectedMpIds, meteringType: this.paneType }));

    if (this.virtualGroupActivated && selectedMpIds.length === 0) {
      this.accountStore.dispatch(new fromAccountStore.FilterSettingReset(this.paneType));
    } else if (this.userSettings) {
      let currentFilter = this.getMeteringFilter(this.paneType);
      if (currentFilter && currentFilter.filterType === MeteringFilterType.VirtualFilter) {
        this.accountStore.dispatch(
          new fromAccountStore.FilterSettingUpdate({
            filter: this.getVirtualFilter(selectedMpIds),
            meteringType: this.paneType,
            userId: this.userInfo.id,
            filterArea: this.filterArea,
            meteringPoints: this.allMeteringPoints
          })
        );
      }
    }

     // Reset scroll position
     this.store.dispatch(new fromStore.ClearScrollPosition());
  };

  mapFromCustomer = (customer: Customer, meteringType: MeteringType): CustomerMeteringView => {
    return <CustomerMeteringView>{
      id: customer.id,
      name: customer.name,
      _expanded: customer._expanded,
      meteringType: meteringType,
      isWhiteLabel: customer.isWhiteLabel,
      externalOrganisationId: customer.externalOrganisationId,
      alias: customer.externalOrganisationAlias,
      meteringPoints:
        meteringType === MeteringType.Cold
          ? [
              ...customer.meteringPointsCold.map((meteringPoint: MeteringPoint) => ({
                ...meteringPoint,
                selected: this.selectedMeteringPointIds.some((x) => x === meteringPoint.id),
                customerName: customer.name,
                isWhiteLabel: customer.isWhiteLabel,
                externalOrganisationId: customer.externalOrganisationId,
                externalOrganisationAlias: customer.externalOrganisationAlias,
              }))
            ]
          : [
              ...customer.meteringPointsHeat.map((meteringPoint: MeteringPoint) => ({
                ...meteringPoint,
                selected: this.selectedMeteringPointIds.some((x) => x === meteringPoint.id),
                customerName: customer.name,
                isWhiteLabel: customer.isWhiteLabel,
                externalOrganisationId: customer.externalOrganisationId,
                externalOrganisationAlias: customer.externalOrganisationAlias,
              }))
            ]
    };
  };

  getMeteringFilter = (meteringType: MeteringType): MeteringFilter => {
    switch (meteringType) {
      case MeteringType.Hot:
        return this.userSettings.hotMeteringFilter;
      case MeteringType.Cold:
        return this.userSettings.coldMeteringFilter;
      case MeteringType.HotLogical:
        return this.userSettings.hotLogicalMeteringFilter;
      case MeteringType.ColdLogical:
        return this.userSettings.coldLogicalMeteringFilter;
    }
  };

  setSortAndPaging(){
    if (!this.tableView)
      return;

    if (this.paneType === MeteringType.Hot) {
      this.store.dispatch(
        new fromStore.SelectPageIndexHotMeteringPoint(
          this.currentPage,
          this.pageSize,
          this.sort.active,
          this.sort.direction,
          this.scrollPosition)
      );
    }

    if (this.paneType === MeteringType.Cold) {
      this.store.dispatch(
        new fromStore.SelectPageIndexColdMeteringPoint(
          this.currentPage,
          this.pageSize,
          this.sort.active,
          this.sort.direction,
          this.scrollPosition)
      );
    }
  }

  viewInformation = (id: number) => {
    this.scrollPosition = this.scroller.getScrollPosition();
    this.setSortAndPaging();

    let selectedMeteringPoint: MeteringPoint;
    let customerName;

    for (let index = 0; index < this.customersWithMeteringPointsView.length; index++) {
      selectedMeteringPoint = this.customersWithMeteringPointsView[index].meteringPoints.find((mp) => mp.id === id);

      if (selectedMeteringPoint) {
         customerName = this.customersWithMeteringPointsView[index].name;
         break;
      }
    }

    this.meteringPointSelected.emit(selectedMeteringPoint);
  };

  sortMeteringPointsByName = (a: MeteringPoint, b: MeteringPoint) => {
    if (a.name > b.name) {
      return 1;
    }

    if (a.name < b.name) {
      return -1;
    }

    return 0;
  };

  onExport(){
    this.isExporting = true;
    let allMpIds: number[] = [];
    let timestamp = this.toLocalDate.transform(new Date(), 'YYYY-MM-dd_H-mm-ss');

    this.customersWithMeteringPointsView.forEach((customer) => {
      allMpIds = [...allMpIds, ...customer.meteringPoints.map((mp) => mp.id)];
    });

    this.subs.sink = this.exportService.exportInFrame(
      this.meteringPointService.createReportKey(),
      (key: string) => this.meteringPointService.exportMeteringPointsToExcel(key, this.paneType, allMpIds),
      () => {
        this.isExporting = false;
        this.changeDetectorRef.detectChanges();
      },
      `Anläggningar_${timestamp}.xlsx`
    );

     // Reset scroll position
     this.store.dispatch(new fromStore.ClearScrollPosition());
  };


  // NOT USED ANYMORE - REMOVE LATER
  toggleView() {
    this.tableView = !this.tableView;
    let tableViewSetting = this.tableView ? UserSettingOption.Active : UserSettingOption.Inactive;

    let newUserSettings = {
      ...this.userSettings,
      meteringpointTableview: tableViewSetting
    };

    // do not try to save virtual filter if it´s included in the user settings
    if (newUserSettings.hotMeteringFilter.filterType === MeteringFilterType.VirtualFilter) {
      newUserSettings.hotMeteringFilter.filterType = MeteringFilterType.Unfiltered;
    }
    if (newUserSettings.coldMeteringFilter.filterType === MeteringFilterType.VirtualFilter) {
      newUserSettings.coldMeteringFilter.filterType = MeteringFilterType.Unfiltered;
    }

    // Reset scroll position
    this.store.dispatch(new fromStore.ClearScrollPosition());

    this.accountStore.dispatch(
      new fromAccountStore.SettingUpdate({
        userSettings: newUserSettings,
        userId: this.userInfo.id,
        filterArea: this.filterArea,
        meteringType: this.paneType,
        meteringPoints: null
      })
    );

    this.isAllSelected();
  }
}
